import * as React from 'react';
import { Location } from 'lib/types';
import classnames from 'classnames';
import { Link } from 'gatsby';
import { Slot } from 'booking/lib/types';
import Loading from 'core/components/Loading';
import Caret from 'assets/caret_down.inline.svg';
import { selectProvider } from 'booking/redux/actions';
import { useDispatch } from 'react-redux';
import NextIcon from '../../DateForm/assets/chevron-right.inline.svg';
import * as styles from './NearbyLocation.module.scss';
import useNearbySlots from './useNearbySlots';
import NearbySlot from './NearbySlot';

export type SelectedSlot = {
    locationId: string;
    locationName?: string;
    time: string;
    provider?: boolean;
    citySlug: string;
    locationSlug: string;
};

type NearbyLocationProps = {
    location: Location;
    selectedSlot: SelectedSlot | null;
    selectSlot: (selectedSlot: SelectedSlot) => void;
    classes?: Partial<typeof styles>;
    onSeeMore?: () => void;
    isMobile?: boolean;
};

export default function NearbyLocation({
    location,
    selectSlot,
    selectedSlot,
    classes,
    onSeeMore,
    isMobile,
}: NearbyLocationProps) {
    const locationId = location._id;
    const locationName = location.name;
    const locationSlug = location.shortSlug ?? '';
    const citySlug = location.city?.slug ?? '';

    const nearbySlots = useNearbySlots(locationId);
    const dispatch = useDispatch();

    const onSelectSlot = React.useCallback(
        (time: Slot['time']) => {
            dispatch(selectProvider(null));

            selectSlot({
                locationId,
                locationName,
                time,
                locationSlug,
                citySlug,
            });
        },
        [
            locationSlug,
            citySlug,
            dispatch,
            selectSlot,
            locationId,
            locationName,
        ],
    );

    const [showMore, setShowMore] = React.useState(false);
    if (!nearbySlots) {
        return null;
    }

    const { isLoading, slots } = nearbySlots;

    if (isLoading) {
        return (
            <div>
                <Loading label="loading nearby locations" />
            </div>
        );
    }

    if (!isLoading && !slots.length) {
        return null;
    }

    const seeMore = onSeeMore ? (
        <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className={classnames(classes?.seeMore, styles.seeMore)}
        >
            <div>See {showMore ? 'less' : 'more'}</div>
            {showMore ? (
                <Caret className={classnames(styles.caret, styles.caretUp)} />
            ) : (
                <Caret className={styles.caret} />
            )}
        </button>
    ) : (
        <Link
            to={`/c/${location.slug}`}
            className={classnames(classes?.seeMore, styles.seeMore)}
        >
            <div>See more</div>
            <NextIcon />
        </Link>
    );

    return (
        <div className={classnames(classes?.root, styles.clinic)}>
            <div className={classnames(classes?.clinicName, styles.clinicName)}>
                {locationName} {!isMobile && slots.length > 4 && seeMore}
            </div>
            <div
                className={classnames(
                    classes?.slotsButtons,
                    styles.slotsButtons,
                )}
                style={showMore ? { height: 'auto' } : {}}
            >
                {slots.map((slot: Slot) => (
                    <NearbySlot
                        onSelect={onSelectSlot}
                        key={slot.time}
                        className={classnames(
                            styles.slotItem,
                            {
                                [styles.isSelected]:
                                    selectedSlot?.locationId === locationId &&
                                    selectedSlot?.time === slot.time,
                            },
                            classes?.slotButton,
                            styles.slotButton,
                        )}
                        slot={slot}
                    />
                ))}
            </div>
            {isMobile && slots.length > 3 && (
                <div className={styles.seeMoreContainer}>{seeMore}</div>
            )}
        </div>
    );
}
